import { AILMENT_CURE, INCURSION, LEVEL_COST_BP, STRUCTURES, type Resources } from '@somnhold/content';

/** Fewer free cells than this inside the walls: time to suggest the Great Work. */
const GREAT_WORK_FREE_CELLS = 12;
import {
  builtMaxHp,
  hexDist,
  hexKey,
  isBuildableTerrain,
  parseHexKey,
  terrainAt,
  type BattleState,
  type CampaignState,
  type Hex,
  buildTerrain,
  cellHex,
  greatWork,
  groupBelow,
  nextDistrict,
  pointsSpent,
  T_INTERIOR,
  heroName,
} from '@somnhold/sim';

/** One suggestion for what to do next in the bastion. `key` changes when the situation does. */
export interface Advice {
  key: string;
  /** i18n key and its variables. */
  msg: string;
  vars?: Record<string, string | number>;
  /** A cell to show, or a panel to open. */
  cell?: Hex;
  act?: 'hero' | 'incursions' | 'board' | 'damage' | 'golems';
}

export interface AdviceCtx {
  me: string;
  now: number;
  day: number;
  canPlay: 'live' | 'preview' | false;
  msToNext: number;
}

const RES = ['wood', 'stone', 'crystal'] as const;
const covers = (have: Resources, cost: Partial<Resources>): boolean => RES.every((k) => have[k] >= (cost[k] ?? 0));
const scaled = (cost: Resources, bp: number): Resources => ({
  wood: Math.trunc((cost.wood * bp) / 10000),
  stone: Math.trunc((cost.stone * bp) / 10000),
  crystal: Math.trunc((cost.crystal * bp) / 10000),
  dust: 0,
  iron: Math.trunc(((cost.iron ?? 0) * bp) / 10000),
});

/** Sector a cell belongs to: the one with the closest gate. */
const sectorAt = (s: CampaignState, at: Hex): number => {
  let best = 0;
  for (const sec of s.map.sectors) if (hexDist(sec.gate, at) < hexDist(s.map.sectors[best]!.gate, at)) best = sec.index;
  return best;
};

/** A free interior cell two or three steps in from a gate, where a first tower does the most good. */
const spotNearGate = (s: CampaignState, town: BattleState, gate: Hex): Hex | null => {
  const R = s.map.radius;
  let best: Hex | null = null;
  let bestD = 99;
  for (let q = -R; q <= R; q++) {
    for (let r = -R; r <= R; r++) {
      if (s.cells[hexKey({ q, r })] || !isBuildableTerrain(terrainAt(town, q, r))) continue;
      const d = hexDist(gate, { q, r });
      if (d >= 2 && d < bestD) {
        bestD = d;
        best = { q, r };
      }
    }
  }
  return best;
};

/**
 * What a player could usefully do right now, most urgent first: defend, gates, repairs, a friend to cure
 * (clerics), unfunded plans, work in progress, a thin sector, skill points, incursions, the board,
 * upgrades, and otherwise wait.
 */
export const advise = (s: CampaignState, town: BattleState, ctx: AdviceCtx): Advice | null => {
  if (s.status !== 'active') return null;
  const me = s.players.find((p) => p.id === ctx.me);
  if (!me) return null;
  if (ctx.canPlay === 'live') return { key: 'defend', msg: 'advice.defend' };

  // Every sector needs its gate built, or the nightmares walk straight in (the ring's bastion: terraced land has
  // no ways in fixed, the group draws its own walls).
  if (!s.map.land)
    for (const sec of s.map.sectors) {
      const cell = s.cells[hexKey(sec.gate)];
      if (!cell?.built && !cell?.job) return { key: `gate:${sec.index}`, msg: 'advice.gate', vars: { n: sec.index + 1 }, cell: sec.gate };
    }

  const cells = Object.entries(s.cells).map(([k, c]) => ({ at: parseHexKey(k), c }));
  // Terraced land, the keep alone: the first towers round the crest, then a palisade round them, then a post by
  // the woods (docs/CONSTRUCCION.md §1).
  if (s.map.land) {
    const has = (pred: (st: string) => boolean): number => cells.filter(({ c }) => !c.part && pred(c.built?.structure ?? c.job?.structure ?? '')).length;
    if (has((st) => STRUCTURES[st as keyof typeof STRUCTURES]?.category === 'tower') < 2) return { key: 'land:towers', msg: 'advice.landTowers', cell: { q: 0, r: 3 } };
    if (has((st) => st === 'wall' || st === 'gate') < 6) return { key: 'land:wall', msg: 'advice.landWall', cell: { q: 0, r: 5 } };
    if (s.mareaIndex >= 1 && has((st) => st === 'depot') === 0) return { key: 'land:depot', msg: 'advice.landDepot', act: 'golems' };
  }
  // What the last wave broke stays broken until someone orders the repair: say how much, worst first.
  const hurt = cells
    .filter(({ c }) => c.built && !c.job && STRUCTURES[c.built.structure].hp > 0 && c.built.hp < builtMaxHp(c.built, ctx.now))
    .map((x) => ({ ...x, ratio: x.c.built!.hp / builtMaxHp(x.c.built!, ctx.now) }))
    .sort((a, b) => a.ratio - b.ratio);
  if (hurt.length) {
    const down = hurt.filter((x) => x.c.built!.hp <= 0).length;
    const worst = hurt[0]!;
    return { key: `damage:${hurt.length}:${down}`, msg: down ? 'advice.damageDown' : 'advice.damage', vars: { n: hurt.length, d: down, s: `structure.${worst.c.built!.structure}` }, ...(hurt.length > 1 ? { act: 'damage' } : { cell: worst.at }) };
  }
  // A cleric with its daily cure ready and a friend carrying a condition: it cures them even if they're away.
  if (me.hero.heroClass === 'cleric' && (me.hero.curedAt === undefined || ctx.now - me.hero.curedAt >= AILMENT_CURE.clericHours * 3_600_000)) {
    const sick = s.players.find((p) => p.id !== me.id && !p.left && p.hero.ailment && p.hero.ailment.until > ctx.now);
    if (sick?.hero.ailment) return { key: `cure:${sick.id}:${sick.hero.ailment.until}`, msg: 'advice.cure', vars: { who: heroName(sick), a: `ailment.${sick.hero.ailment.kind}` }, act: 'hero' };
  }

  // Plans waiting for materials the store can cover (gates first).
  const plans = cells
    .filter(({ c }) => c.job?.stage === 'blueprint' && RES.some((k) => (c.job!.cost[k] ?? 0) > (c.job!.paid[k] ?? 0) && s.storage[k] > 0))
    .sort((a, b) => Number(b.c.job!.structure === 'gate') - Number(a.c.job!.structure === 'gate'));
  const plan = plans[0];
  if (plan) return { key: `fund:${hexKey(plan.at)}`, msg: 'advice.fund', vars: { s: plan.c.job!.kind === 'district' ? 'structure.greatWork' : `structure.${plan.c.job!.structure}` }, cell: plan.at };

  const work = cells.find(({ c }) => c.job?.stage === 'building');
  if (work) return { key: `work:${hexKey(work.at)}`, msg: 'advice.work', vars: { s: work.c.job!.kind === 'district' ? 'structure.greatWork' : `structure.${work.c.job!.structure}` }, cell: work.at };

  // Running out of room: the Great Work adds a district (once everyone active is up to it).
  if (!s.map.land && !greatWork(s) && nextDistrict(s) && !groupBelow(s, ctx.now)) {
    const terrain = buildTerrain(s.map);
    let free = 0;
    terrain.forEach((tr, id) => {
      if (tr === T_INTERIOR && !s.cells[hexKey(cellHex(s.map.radius, id))]) free++;
    });
    if (free < GREAT_WORK_FREE_CELLS) return { key: `great:${s.map.districts?.length ?? 0}`, msg: 'advice.greatWork', cell: { q: 0, r: 0 } };
  }

  // The sector with the fewest towers, if it has fewer than two and the store can pay a ballista.
  const towers = s.map.sectors.map(() => 0);
  for (const { at, c } of cells) {
    const st = c.built?.structure ?? c.job?.structure;
    if (st && STRUCTURES[st].category === 'tower') towers[sectorAt(s, at)]! += 1;
  }
  let weak = 0;
  towers.forEach((n, i) => {
    if (n < (towers[weak] as number)) weak = i;
  });
  if (!s.map.land && (towers[weak] as number) < 2 && covers(s.storage, STRUCTURES.ballista.cost)) {
    const spot = spotNearGate(s, town, s.map.sectors[weak]!.gate);
    if (spot) return { key: `tower:${weak}:${towers[weak]}`, msg: 'advice.tower', vars: { n: weak + 1 }, cell: spot };
  }

  // skillPoints counts every point earned; only what is left after the learned skills is free.
  const free = me.hero.skillPoints - pointsSpent(me.hero);
  if (free > 0) return { key: `skills:${free}`, msg: 'advice.skills', vars: { n: free }, act: 'hero' };

  const today = me.incursionDay === ctx.day ? me.incursionsToday : 0;
  if (today < INCURSION.fullPerDay && ctx.msToNext > 10 * 60_000) {
    return { key: `incursion:${ctx.day}:${today}`, msg: 'advice.incursion', vars: { n: INCURSION.fullPerDay - today }, act: 'incursions' };
  }

  const task = s.board.find((b) => !b.done && b.by !== ctx.me);
  if (task) return { key: `board:${task.id}`, msg: 'advice.board', act: 'board' };

  const up = cells.find(({ c }) => {
    const b = c.built;
    if (!b || c.job || STRUCTURES[b.structure].category !== 'tower' || b.level >= 3) return false;
    return covers(s.storage, scaled(STRUCTURES[b.structure].cost, LEVEL_COST_BP[b.level as 1 | 2]));
  });
  if (up) return { key: `upgrade:${hexKey(up.at)}`, msg: 'advice.upgrade', vars: { s: `structure.${up.c.built!.structure}` }, cell: up.at };

  return { key: 'wait', msg: ctx.canPlay === 'preview' ? 'advice.preview' : 'advice.wait' };
};
